import React from 'react'
import { Typography, Box, TextField, Slider } from '@mui/material'
import { BudgetStepProps } from '../types'

const BudgetStep: React.FC<BudgetStepProps> = ({ min, max, onChange }) => {
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      onChange(newValue[0], newValue[1])
    }
  }

  const handleMinInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    const value = inputValue === '' ? 0 : parseInt(inputValue, 10) || 0
    onChange(value, max)
  }

  const handleMaxInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    const value = inputValue === '' ? 0 : parseInt(inputValue, 10) || 0
    onChange(min, value)
  }

  return (
    <Box className="flex flex-col gap-6">
      <Box className="flex flex-col gap-2">
        <Typography variant="h5" className="font-bold text-gray-900">
          What&apos;s your budget range?
        </Typography>
      </Box>

      <Box className="flex flex-col gap-4">
        {/* Input Fields */}
        <Box className="flex items-center gap-4">
          <Box className="flex-1">
            <Typography variant="body1" className="font-semibold text-gray-900 mb-2">
              Min
            </Typography>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium z-10">$</span>
              <TextField
                type="number"
                value={min === 0 ? '' : min}
                onChange={handleMinInputChange}
                placeholder="0"
                fullWidth
                variant="outlined"
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#e0e2ef',
                    },
                    '&:hover fieldset': {
                      borderColor: '#c1c7d0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#050a22',
                      borderWidth: '1px',
                    },
                    '& input': {
                      paddingLeft: '28px',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#050a22',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 14px',
                  },
                }}
              />
            </div>
          </Box>
          <Typography variant="body1" className="text-gray-400 font-medium mt-8">
            To
          </Typography>
          <Box className="flex-1">
            <Typography variant="body1" className="font-semibold text-gray-900 mb-2">
              Max
            </Typography>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium z-10">$</span>
              <TextField
                type="number"
                value={max === 0 ? '' : max}
                onChange={handleMaxInputChange}
                placeholder="2300"
                fullWidth
                variant="outlined"
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    '& fieldset': {
                      borderColor: '#e0e2ef',
                    },
                    '&:hover fieldset': {
                      borderColor: '#c1c7d0',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#050a22',
                      borderWidth: '1px',
                    },
                    '& input': {
                      paddingLeft: '28px',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#050a22',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 14px',
                  },
                }}
              />
            </div>
          </Box>
        </Box>

        {/* Slider */}
        <Box className="pt-2 pb-2 px-2">
          <Slider
            value={[min, max]}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            valueLabelFormat={value => `$${value}`}
            min={0}
            max={5000}
            step={50}
            sx={{
              color: '#050a22',
              '& .MuiSlider-thumb': {
                backgroundColor: '#050a22',
                width: 20,
                height: 20,
              },
              '& .MuiSlider-track': {
                backgroundColor: '#050a22',
                height: 6,
              },
              '& .MuiSlider-rail': {
                backgroundColor: '#e0e2ef',
                height: 6,
              },
            }}
          />
        </Box>

        {/* Range Labels */}
        <Box className="flex justify-between text-xs text-gray-400 font-medium px-1">
          <span>$0</span>
          <span>$5000+</span>
        </Box>
      </Box>
    </Box>
  )
}

export default BudgetStep
